// Single source of truth for online-presence in the UI. EVERY surface that shows a green/
// amber/gray "online" dot or "en línea / hace Xm" label MUST use this — no inline thresholds.
// Mirrors the backend model (backend/src/utils/presence.js): online = active < 3 min & not
// idle, away = any ping < 10 min, offline = otherwise. Thresholds live here once.
//
// Prefer the backend-computed `presence` field when the row carries it (agents/clients from
// the unified endpoints already do). Fall back to computing from timestamps so older payloads
// and socket rows still render consistently.
const ONLINE_MS = 3 * 60 * 1000;
const AWAY_MS = 10 * 60 * 1000;

export const PRESENCE_COLORS = {
  online: '#10b981',
  away: '#f59e0b',
  offline: '#94a3b8',
};

// Freshest "active" signal across the two heartbeats (agents: last_seen_at; everyone:
// last_activity_at) and the tab-alive ping (last_ping_at). Accepts snake_case or camelCase.
function activeMs(u = {}) {
  const pick = (a, b) => u[a] ?? u[b];
  const ts = [
    pick('last_seen_at', 'lastSeenAt'),
    pick('last_activity_at', 'lastActivityAt'),
    pick('last_login', 'lastLogin'),
  ];
  return Math.max(0, ...ts.map((v) => (v ? new Date(v).getTime() : 0)));
}
function aliveMs(u = {}) {
  const pick = (a, b) => u[a] ?? u[b];
  return Math.max(activeMs(u), (pick('last_ping_at', 'lastPingAt') ? new Date(pick('last_ping_at', 'lastPingAt')).getTime() : 0));
}

// Returns 'online' | 'away' | 'offline'.
export function presenceStatus(u = {}) {
  if (u.presence === 'online' || u.presence === 'away' || u.presence === 'offline') return u.presence;
  const idle = (u.idle_since ?? u.idleSince) != null;
  const now = Date.now();
  if (!idle && activeMs(u) > now - ONLINE_MS) return 'online';
  if (aliveMs(u) > now - AWAY_MS) return 'away';
  return 'offline';
}

export function presenceColor(u) {
  const status = typeof u === 'string' ? u : presenceStatus(u);
  return PRESENCE_COLORS[status] || PRESENCE_COLORS.offline;
}

// Localized short status word. Pass the i18n t(). Uses common.* keys shared app-wide.
export function presenceStatusLabel(status, t) {
  if (status === 'online') return t('presence.online');
  if (status === 'away') return t('presence.away');
  return t('presence.offline');
}

// The best timestamp to feed a relative-time formatter ("hace 5 min"). Null if never seen.
export function lastActiveDate(u = {}) {
  const ms = activeMs(u);
  return ms > 0 ? new Date(ms) : null;
}
